import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, IconButton, Drawer, List, ListItemButton, ListItemText, Grid } from '@mui/material';
import {
  LocalHospital as HospitalIcon, Menu as MenuIcon, Close as CloseIcon,
  ArrowForward as ArrowIcon, PlayCircleOutline as ExploreIcon,
} from '@mui/icons-material';
import { publicAPI } from '../services/api';
import { COLLEGE_NAME, PRODUCT_NAME } from '../branding';
import { Reveal, HOME_COLORS, CountUp, WaveDivider } from './home/common';
import HeroSlider from './home/HeroSlider';
import AboutSection from './home/AboutSection';
import FeaturesSection from './home/FeaturesSection';
import LeadershipSection from './home/LeadershipSection';
import ProgramsSection, { PROGRAMS } from './home/ProgramsSection';
import GallerySection from './home/GallerySection';
import MapContactSection from './home/MapContactSection';
import AdmissionSection from './home/AdmissionSection';

const NAV_LINKS = [
  { label: 'About', id: 'about' },
  { label: 'Programs', id: 'programs' },
  { label: 'Gallery', id: 'gallery' },
  { label: 'Leadership', id: 'leadership' },
  { label: 'Contact', id: 'contact' },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function HomePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [presetProgram, setPresetProgram] = useState('');

  useEffect(() => {
    publicAPI.getCollegeProfile().then((r) => setProfile(r.data)).catch(() => {});
    publicAPI.getGallery().then((r) => setPosts(r.data)).catch(() => {});
    publicAPI.getLeadership().then((r) => setLeaders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const heroImages = useMemo(() => {
    const all = [];
    (posts || []).forEach((post) => (post.photos || []).forEach((photo) => all.push({ url: `/api${photo.url}` })));
    return all.slice(0, 6);
  }, [posts]);

  const galleryPhotoCount = useMemo(
    () => (posts || []).reduce((sum, p) => sum + (p.photos || []).length, 0),
    [posts]
  );

  const goApply = (programName) => {
    setPresetProgram(programName || '');
    setMobileOpen(false);
    setTimeout(() => scrollToId('admissions'), 60);
  };

  const collegeName = profile?.name || COLLEGE_NAME;

  return (
    <Box sx={{ bgcolor: '#fff', overflowX: 'hidden' }}>
      {/* Navbar */}
      <Box
        component="nav"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20,
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease',
          bgcolor: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          boxShadow: scrolled ? '0 4px 20px rgba(0,37,26,0.08)' : 'none',
          backdropFilter: scrolled ? 'saturate(180%) blur(8px)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: scrolled ? 1.2 : 2, transition: 'padding 0.3s ease' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${HOME_COLORS.dark}, ${HOME_COLORS.sky})`,
              }}>
                <HospitalIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: scrolled ? HOME_COLORS.dark : '#fff', fontSize: '1.05rem', letterSpacing: 0.2, transition: 'color 0.3s ease' }}>
                {collegeName}
              </Typography>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
              {NAV_LINKS.map((l) => (
                <Typography
                  key={l.id} onClick={() => scrollToId(l.id)}
                  sx={{
                    position: 'relative', cursor: 'pointer', fontWeight: 600, fontSize: '0.92rem', pb: 0.5,
                    color: scrolled ? '#3d4f4c' : 'rgba(255,255,255,0.92)',
                    transition: 'color 0.3s ease',
                    '&::after': {
                      content: '""', position: 'absolute', left: 0, bottom: 0, height: 2, width: 0,
                      bgcolor: scrolled ? HOME_COLORS.sky : '#fff', transition: 'width 0.3s ease',
                    },
                    '&:hover': { color: scrolled ? HOME_COLORS.mid : '#fff' },
                    '&:hover::after': { width: '100%' },
                  }}
                >
                  {l.label}
                </Typography>
              ))}
              <Button
                variant="contained" onClick={() => goApply()}
                sx={{
                  borderRadius: 2.5, px: 2.5, fontWeight: 700,
                  background: scrolled ? `linear-gradient(135deg, ${HOME_COLORS.dark}, ${HOME_COLORS.sky})` : 'rgba(255,255,255,0.16)',
                  border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.4)',
                  animation: 'pulseGlow 2.6s infinite',
                  '&:hover': { background: scrolled ? `linear-gradient(135deg, ${HOME_COLORS.mid}, ${HOME_COLORS.sky})` : 'rgba(255,255,255,0.28)' },
                }}
              >
                Apply Now
              </Button>
              <Button onClick={() => navigate('/login')} sx={{ fontWeight: 600, color: scrolled ? '#3d4f4c' : 'rgba(255,255,255,0.9)' }}>
                Staff Login
              </Button>
            </Box>

            <IconButton sx={{ display: { xs: 'flex', md: 'none' }, color: scrolled ? HOME_COLORS.dark : '#fff' }} onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
            <IconButton onClick={() => setMobileOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <List>
            {NAV_LINKS.map((l) => (
              <ListItemButton key={l.id} onClick={() => { scrollToId(l.id); setMobileOpen(false); }}>
                <ListItemText primary={l.label} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={() => goApply()}>
              <ListItemText primary="Apply Now" primaryTypographyProps={{ fontWeight: 700, color: HOME_COLORS.dark }} />
            </ListItemButton>
            <ListItemButton onClick={() => navigate('/login')}>
              <ListItemText primary="Staff Login" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Hero */}
      <Box sx={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', color: '#fff' }}>
        <HeroSlider images={heroImages} />

        <Box sx={{ position: 'absolute', width: 560, height: 560, borderRadius: '50%', bgcolor: 'rgba(125,211,252,0.10)', top: -180, right: -160, animation: 'floatYSlow 9s ease-in-out infinite', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', bottom: -140, left: -100, animation: 'floatYSlow 11s ease-in-out infinite reverse', zIndex: 1 }} />

        <Container maxWidth="lg" sx={{ position: 'relative', pt: { xs: 14, md: 10 }, pb: 8, zIndex: 2 }}>
          <Reveal>
            <Typography
              sx={{
                display: 'inline-block', px: 2, py: 0.6, borderRadius: 5, mb: 3,
                bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(125,211,252,0.4)',
                fontWeight: 600, fontSize: '0.85rem', letterSpacing: 0.5,
              }}
            >
              Admissions Open · {PRODUCT_NAME}
            </Typography>
          </Reveal>
          <Reveal delay={100}>
            <Typography variant="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.4rem', sm: '3.2rem', md: '4rem' }, lineHeight: 1.12, maxWidth: 780, mb: 3 }}>
              Shaping compassionate,{' '}
              <Box component="span" sx={{ background: `linear-gradient(90deg, ${HOME_COLORS.skyLight}, #fff, ${HOME_COLORS.skyLight})`, backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmerMove 5s linear infinite' }}>
                skilled nursing
              </Box>{' '}
              professionals
            </Typography>
          </Reveal>
          <Reveal delay={200}>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, opacity: 0.9, maxWidth: 620, mb: 5, lineHeight: 1.7 }}>
              {collegeName} offers recognized nursing programs built on rigorous academics, hands-on clinical training and dedicated faculty support.
            </Typography>
          </Reveal>
          <Reveal delay={300}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 7 }}>
              <Button
                size="large" variant="contained" onClick={() => goApply()} endIcon={<ArrowIcon />}
                sx={{ bgcolor: '#fff', color: HOME_COLORS.dark, fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, '&:hover': { bgcolor: HOME_COLORS.skyPale } }}
              >
                Apply for Admission
              </Button>
              <Button
                size="large" variant="outlined" onClick={() => scrollToId('programs')} startIcon={<ExploreIcon />}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
              >
                Explore Programs
              </Button>
            </Box>
          </Reveal>

          <Reveal delay={400}>
            <Grid container spacing={3} sx={{ maxWidth: 560 }}>
              <Grid item xs={4}>
                <Typography variant="h3" sx={{ fontWeight: 800 }}><CountUp value={PROGRAMS.length} suffix="+" /></Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Nursing Programs</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h3" sx={{ fontWeight: 800 }}><CountUp value={galleryPhotoCount} /></Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Campus Moments</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>100%</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Clinical Exposure</Typography>
              </Grid>
            </Grid>
          </Reveal>
        </Container>
      </Box>
      <WaveDivider fill="#ffffff" />

      <AboutSection profile={profile} posts={posts} />
      <FeaturesSection />
      <ProgramsSection onApply={goApply} />
      <GallerySection posts={posts} />
      <LeadershipSection people={leaders} />
      <MapContactSection profile={profile} />
      <AdmissionSection presetProgram={presetProgram} />

      {/* Footer */}
      <Box sx={{ bgcolor: HOME_COLORS.deep, color: 'rgba(255,255,255,0.75)', py: 5 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <HospitalIcon sx={{ color: HOME_COLORS.sky }} />
              <Typography sx={{ fontWeight: 700, color: '#fff' }}>{collegeName}</Typography>
            </Box>
            <Typography variant="body2">© {new Date().getFullYear()} {collegeName}. All rights reserved.</Typography>
            <Button size="small" onClick={() => navigate('/login')} sx={{ color: 'rgba(255,255,255,0.7)' }}>Staff / Admin Login</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
